import type { Role } from './enums.js';

// Katalog hak akses (RBAC) — satu sumber kebenaran untuk server (penjaga endpoint) & aplikasi (menu/tombol).
// Owner mengatur peran dengan mencentang hak dari katalog ini; hak baru hanya bisa ditambah lewat kode karena setiap hak
// harus punya pemeriksaan di server.
//
// Jenis dasar (users.role) menentukan CAKUPAN DATA & halaman awal dan tidak bisa diatur:
//   owner = semua hak (terkunci) · admin = semua data · sales = data miliknya · driver = nota tugasnya ·
//   toko = portal tokonya (terkunci, hak tetap).
// Peran kustom memakai jenis dasar admin, sales atau driver.

/** Jenis dasar yang bisa dipakai peran kustom. */
export const CUSTOM_BASES = ['admin', 'sales', 'driver'] as const satisfies readonly Role[];
export type CustomBase = (typeof CUSTOM_BASES)[number];

export interface PermDef {
  key: string;
  group: string;
  label: string;
  /** Jenis dasar yang bisa diberi hak ini (cakupan data & logikanya hanya ada untuk jenis ini). */
  bases: readonly CustomBase[];
  /** Hak lain yang otomatis ikut (layar ini memanggil data tersebut). */
  implies?: readonly string[];
}

const A = ['admin'] as const;
const AS = ['admin', 'sales'] as const;

export const PERMISSIONS = [
  { key: 'beranda.lihat', group: 'Beranda & Operasi', label: 'Lihat beranda & ringkasan', bases: AS },
  {
    key: 'operasi.lihat',
    group: 'Beranda & Operasi',
    label: 'Lihat operasi harian',
    bases: A,
    implies: ['nota.lihat', 'rute.lihat'],
  },
  { key: 'spo.lihat', group: 'SPO', label: 'Lihat SPO', bases: AS },
  { key: 'spo.buat', group: 'SPO', label: 'Buat SPO', bases: AS, implies: ['spo.lihat', 'toko.lihat'] },
  { key: 'spo.batal', group: 'SPO', label: 'Batalkan SPO (belum diproses)', bases: AS, implies: ['spo.lihat'] },
  { key: 'spo.verifikasi', group: 'SPO', label: 'Verifikasi / tolak SPO jadi nota', bases: A, implies: ['spo.lihat'] },
  { key: 'nota.lihat', group: 'Nota & Pengiriman', label: 'Lihat nota', bases: ['admin', 'sales', 'driver'] },
  { key: 'nota.ubah', group: 'Nota & Pengiriman', label: 'Ubah / batalkan nota', bases: A, implies: ['nota.lihat'] },
  { key: 'nota.muat', group: 'Nota & Pengiriman', label: 'Muat barang ke driver', bases: A, implies: ['nota.lihat'] },
  {
    key: 'nota.kirim',
    group: 'Nota & Pengiriman',
    label: 'Serah terima / gagal kirim',
    bases: ['admin', 'driver'],
    implies: ['nota.lihat'],
  },
  {
    key: 'nota.kembali',
    group: 'Nota & Pengiriman',
    label: 'Nota kembali & selesaikan gagal kirim',
    bases: A,
    implies: ['nota.lihat'],
  },
  { key: 'rute.lihat', group: 'Rute Kirim', label: 'Lihat rute & surat jalan', bases: A, implies: ['nota.lihat'] },
  { key: 'rute.kelola', group: 'Rute Kirim', label: 'Buat / ubah / batal / tutup rute', bases: A, implies: ['rute.lihat'] },
  { key: 'peta.lihat', group: 'Peta', label: 'Peta live driver & sales', bases: A },
  {
    key: 'keuangan.lihat',
    group: 'Keuangan',
    label: 'Lihat setoran, transfer & piutang',
    bases: A,
    implies: ['nota.lihat'],
  },
  { key: 'keuangan.bayar', group: 'Keuangan', label: 'Catat pembayaran nota', bases: A, implies: ['nota.lihat'] },
  {
    key: 'keuangan.transfer',
    group: 'Keuangan',
    label: 'Konfirmasi / tolak transfer',
    bases: A,
    implies: ['keuangan.lihat'],
  },
  {
    key: 'keuangan.setoran',
    group: 'Keuangan',
    label: 'Terima setoran cash driver',
    bases: A,
    implies: ['keuangan.lihat'],
  },
  {
    key: 'kunjungan.lihat',
    group: 'Kunjungan (DKS)',
    label: 'Lihat kunjungan & rekap',
    bases: AS,
    implies: ['toko.lihat'],
  },
  {
    key: 'kunjungan.checkin',
    group: 'Kunjungan (DKS)',
    label: 'Check-in kunjungan & berbagi lokasi',
    bases: AS,
    implies: ['kunjungan.lihat', 'toko.lihat'],
  },
  {
    key: 'kunjungan.target',
    group: 'Kunjungan (DKS)',
    label: 'Atur target kunjungan',
    bases: A,
    implies: ['kunjungan.lihat'],
  },
  {
    key: 'kunjungan.periksa',
    group: 'Kunjungan (DKS)',
    label: 'Periksa kunjungan janggal ("Sudah dicek")',
    bases: A,
    implies: ['kunjungan.lihat'],
  },
  { key: 'toko.lihat', group: 'Toko', label: 'Lihat toko', bases: AS },
  { key: 'toko.tambah', group: 'Toko', label: 'Tambah toko', bases: AS, implies: ['toko.lihat'] },
  { key: 'toko.ubah', group: 'Toko', label: 'Ubah data toko', bases: AS, implies: ['toko.lihat'] },
  {
    key: 'toko.kredit',
    group: 'Toko',
    label: 'Atur termin, batas kredit, sales & status toko',
    bases: A,
    implies: ['toko.ubah'],
  },
  { key: 'toko.titik', group: 'Toko', label: 'Pindah titik lokasi toko', bases: A, implies: ['toko.ubah'] },
  { key: 'laporan.lihat', group: 'Laporan', label: 'Lihat rekap penjualan', bases: AS },
  { key: 'laporan.hpp', group: 'Laporan', label: 'Lihat HPP, margin & nilai stok', bases: A },
  { key: 'stok.lihat', group: 'Stok', label: 'Lihat stok & kartu stok', bases: A },
  { key: 'stok.masuk', group: 'Stok', label: 'Catat barang masuk', bases: A, implies: ['stok.lihat'] },
  { key: 'stok.opname', group: 'Stok', label: 'Stok opname / penyesuaian', bases: A, implies: ['stok.lihat'] },
  { key: 'biaya.lihat', group: 'Biaya', label: 'Lihat biaya operasional', bases: A },
  { key: 'biaya.tambah', group: 'Biaya', label: 'Tambah biaya', bases: A, implies: ['biaya.lihat'] },
  { key: 'biaya.hapus', group: 'Biaya', label: 'Hapus biaya', bases: A, implies: ['biaya.lihat'] },
  { key: 'produk.lihat', group: 'Produk & Harga', label: 'Lihat produk & harga', bases: A },
  {
    key: 'produk.ubah',
    group: 'Produk & Harga',
    label: 'Tambah / ubah produk, harga & diskon',
    bases: A,
    implies: ['produk.lihat'],
  },
  {
    key: 'master.kelola',
    group: 'Data Master',
    label: 'Kelola Data Master (satuan, divisi, area, kategori biaya, rekening, alasan)',
    bases: A,
  },
  {
    key: 'tugas.antar',
    group: 'Pengiriman (driver)',
    label: 'Tugas antar: muat, perjalanan, navigasi & GPS',
    bases: ['driver'],
    implies: ['nota.lihat'],
  },
] as const satisfies readonly PermDef[];

export type Perm = (typeof PERMISSIONS)[number]['key'];
export const PERM_KEYS: readonly Perm[] = PERMISSIONS.map((p) => p.key);
const BY_KEY = new Map<string, PermDef>(PERMISSIONS.map((p) => [p.key, p]));
export const permDef = (k: string): PermDef | undefined => BY_KEY.get(k);

/** Hak yang boleh dimiliki jenis dasar ini. */
export const permsForBase = (base: Role): Perm[] =>
  base === 'owner'
    ? [...PERM_KEYS]
    : (PERMISSIONS as readonly PermDef[]).filter((p) => (p.bases as readonly string[]).includes(base)).map((p) => p.key as Perm);

/** Rapikan daftar hak: buang yang tidak dikenal / tidak berlaku untuk jenis dasar, tambahkan hak turunan (implies). */
export function normalizePerms(base: Role, perms: readonly string[]): Perm[] {
  if (base === 'owner') return [...PERM_KEYS];
  if (base === 'toko') return [];
  const ok = new Set<string>(permsForBase(base));
  const out = new Set<string>();
  const add = (k: string) => {
    if (!ok.has(k) || out.has(k)) return;
    out.add(k);
    for (const i of permDef(k)?.implies ?? []) add(i);
  };
  for (const k of perms) add(k);
  return PERM_KEYS.filter((k) => out.has(k));
}

/** Hak bawaan peran sistem — sama persis dengan hak akses sebelum peran bisa diatur. */
export const DEFAULT_PERMS: Record<Role, readonly Perm[]> = {
  owner: PERM_KEYS,
  // Daftar pilihan sebelumnya hanya bisa diubah owner (Pengaturan) → peran Admin bawaan tidak otomatis mendapatkannya.
  admin: permsForBase('admin').filter((p) => p !== 'master.kelola'),
  sales: normalizePerms('sales', [
    'beranda.lihat',
    'spo.lihat',
    'spo.buat',
    'spo.batal',
    'nota.lihat',
    'kunjungan.lihat',
    'kunjungan.checkin',
    'toko.lihat',
    'toko.tambah',
    'toko.ubah',
    'laporan.lihat',
  ]),
  driver: normalizePerms('driver', ['nota.lihat', 'nota.kirim', 'tugas.antar']),
  toko: [],
};

/** Grup katalog berurutan (untuk layar pengaturan peran). */
export const PERM_GROUPS: string[] = [...new Set(PERMISSIONS.map((p) => p.group))];
